import axios from 'axios'
import config from 'config'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { TABLE_DIRECTION_ASC, TABLE_DIRECTION_DESC } from 'lib/tableLib'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useSnackbar from '../Snackbar/useSnackbar'

import {
  getUsersTypeList,
  USERS_LIST_TYPE_SIXT_USERS,
  USERS_LIST_TYPE_PARTNER_USERS,
  USERS_STATES_LIST,
} from './usersLib'

const useUsersFilters = (listType) => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const defaultFilters = useMemo(
    () =>
      [USERS_LIST_TYPE_SIXT_USERS, USERS_LIST_TYPE_PARTNER_USERS].includes(
        listType
      )
        ? {
            editorQuery: '',
            nameQuery: '',
            surnameQuery: '',
            emailQuery: '',
            createdAtFrom: null,
            createdAtTo: null,
            updatedAtFrom: null,
            updatedAtTo: null,
            partners: [OPTION_ALL_VALUE],
            account: [OPTION_ALL_VALUE],
            states: OPTION_ALL_VALUE,
            order: { column: 'email', direction: TABLE_DIRECTION_ASC },
          }
        : {
            partnerIdQuery: '',
            partnerNameQuery: '',
            createdAtFrom: null,
            createdAtTo: null,
            updatedAtFrom: null,
            updatedAtTo: null,
            states: OPTION_ALL_VALUE,
            order: { column: 'partnerGroup', direction: TABLE_DIRECTION_ASC },
          },
    [listType]
  )

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    error: null,
  })

  const [filters, setFilters] = useState()

  useEffect(() => {
    setFilters({
      ...defaultFilters,
      initialized: false,
    })
  }, [setFilters, defaultFilters])

  const [filtersData, setFiltersData] = useState({
    partners: [allOption],
    account: [allOption],
    states: [allOption],
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error || null,
      }))
    },
    [setState]
  )

  const setSuccessFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: false,
      error: null,
    }))
  }, [])

  const resetFilters = useCallback(() => {
    setFilters((s) => ({
      ...s,
      ...defaultFilters,
    }))
  }, [defaultFilters])

  const fetchFiltersData = useCallback(async () => {
    setStartFetching()

    try {
      const results = await axios.get(`${config.apiBaseUrl}filters/users`)

      setSuccessFetching(results.data.body)

      return results.data.body
    } catch (e) {
      let message =
        e.response?.data?.message ||
        t('common.error.fetchData', {
          attribute: e.response?.status,
        })

      setStopFetching(e)

      showSnackbarMessage({
        severity: 'error',
        message,
      })
    }
  }, [
    setStartFetching,
    setStopFetching,
    setSuccessFetching,
    showSnackbarMessage,
    t,
  ])

  const initFilters = useCallback(
    async (listType) => {
      let data
      data = await fetchFiltersData()

      setFiltersData((s) => ({
        ...s,
        partners: [
          allOption,
          ...(data?.partners?.map((partner) =>
            getOption(partner.name, partner.id)
          ) || []),
        ],
        account: [
          allOption,
          ...getUsersTypeList(listType).map((account) =>
            getOption(t(`users.accountTypes.${account}`), account)
          ),
        ],
        states: [
          allOption,
          ...USERS_STATES_LIST.map((state) =>
            getOption(t(`users.status.${state}`), state)
          ),
        ],
      }))

      setFilters({
        ...defaultFilters,
        initialized: true,
      })
    },
    [allOption, defaultFilters, fetchFiltersData, t]
  )

  const setGenericFilter = useCallback(
    (name, value) => {
      setFilters((s) => ({ ...s, [name]: value }))
    },
    [setFilters]
  )

  const setEditorQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, editorQuery: value }))
    },
    [setFilters]
  )
  const setNameQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, nameQuery: value }))
    },
    [setFilters]
  )
  const setSurnameQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, surnameQuery: value }))
    },
    [setFilters]
  )
  const setEmailQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, emailQuery: value }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const setAccount = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, account: value }))
    },
    [setFilters]
  )

  const setStates = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, states: value }))
    },
    [setFilters]
  )

  const setCreatedAtRange = useCallback(
    (createdAtFrom, createdAtTo) => {
      setFilters((s) => ({ ...s, createdAtFrom, createdAtTo }))
    },
    [setFilters]
  )

  const setUpdatedAtRange = useCallback(
    (updatedAtFrom, updatedAtTo) => {
      setFilters((s) => ({ ...s, updatedAtFrom, updatedAtTo }))
    },
    [setFilters]
  )

  const setSort = useCallback(
    (value) => {
      setFilters((s) => ({
        ...s,
        order: {
          column: value,
          direction:
            s.order.column !== value ||
            s.order.direction !== TABLE_DIRECTION_ASC
              ? TABLE_DIRECTION_ASC
              : TABLE_DIRECTION_DESC,
        },
      }))
    },
    [setFilters]
  )

  return {
    filters,
    filtersData,
    initFilters,
    setEditorQuery,
    setNameQuery,
    setSurnameQuery,
    setEmailQuery,
    setCreatedAtRange,
    setUpdatedAtRange,
    setPartners,
    setAccount,
    setStates,
    setSort,
    resetFilters,
    initialized: filters?.initialized,
    isFetching: state.isFetching,
    error: state.error,
    setGenericFilter,
  }
}
export default useUsersFilters
